import { useState, useEffect } from "react";
import { t, font, Panel, Button, Input, Avatar, Badge, EmptyState } from "@realtyflows/ui";
import {
  searchTeams, requestToJoinTeam, getMyJoinRequests, cancelJoinRequest,
  getIncomingJoinRequests, approveJoinRequest, denyJoinRequest,
} from "../api";

/**
 * TeamJoinRequests — V1 panel embedded on the Team page.
 *
 * Two modes (auto-detected from `user.team_id` and `isBroker`):
 *
 *   1. Agent without a team:
 *      - Search broker teams by name/brokerage
 *      - Submit a join request (with optional note)
 *      - View / cancel their own pending requests
 *
 *   2. Broker (team owner):
 *      - See incoming pending requests from agents
 *      - Approve (assigns the agent to the team) or Deny
 */
export default function TeamJoinRequests({ user, isBroker, onChange }) {
  const isAgentLooking = !user?.team_id && !isBroker;

  return isBroker ? (
    <BrokerInbox onChange={onChange} />
  ) : isAgentLooking ? (
    <AgentSearchAndRequests onChange={onChange} />
  ) : null;
}

/* ─────────────── Broker view: incoming requests ─────────────── */

function BrokerInbox({ onChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { setRequests(await getIncomingJoinRequests()); }
    catch { setRequests([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === "approve") await approveJoinRequest(id);
      else await denyJoinRequest(id);
      await load();
      if (onChange) onChange();
    } catch (err) {
      window.alert(err.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;

  return (
    <Panel padding={20} style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
          Pending Join Requests
        </span>
        <Badge color={requests.length > 0 ? t.amber : t.textMuted}>
          {requests.length}
        </Badge>
      </div>

      {requests.length === 0 ? (
        <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          No pending requests. Agents who want to join your team will appear here.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {requests.map((r) => (
            <div key={r.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: 12, background: t.surfaceWarm,
              border: `1px solid ${t.border}`, borderRadius: 8,
            }}>
              <Avatar src={r.photo_url} name={r.name} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{r.name}</div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                  {r.email}{r.brokerage ? ` · ${r.brokerage}` : ""}
                </div>
                {r.message && (
                  <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 4, fontStyle: "italic", fontFamily: font.body }}>
                    "{r.message}"
                  </div>
                )}
              </div>
              <Button variant="primary" size="sm" disabled={busyId === r.id} onClick={() => handleAction(r.id, "approve")}>Approve</Button>
              <Button variant="secondary" size="sm" disabled={busyId === r.id} onClick={() => handleAction(r.id, "deny")}>Deny</Button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ─────────────── Agent view: search + request ─────────────── */

function AgentSearchAndRequests({ onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mine, setMine] = useState([]);
  const [message, setMessage] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadMine = async () => {
    try { setMine(await getMyJoinRequests()); }
    catch { setMine([]); }
  };
  useEffect(() => { loadMine(); }, []);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchTeams(query.trim())
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const handleRequest = async () => {
    if (!selectedTeam) return;
    setSubmitting(true);
    try {
      await requestToJoinTeam(selectedTeam.id, message);
      setSelectedTeam(null);
      setMessage("");
      setQuery("");
      setResults([]);
      await loadMine();
      if (onChange) onChange();
    } catch (err) {
      window.alert(err.message || "Failed to send request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await cancelJoinRequest(id);
      await loadMine();
    } catch (err) {
      window.alert(err.message || "Failed to cancel");
    }
  };

  return (
    <Panel padding={20} style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.display }}>
        Join a Brokerage Team
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body }}>
        Search by team or brokerage name and request to join. The team owner will see your request and approve or deny.
      </div>

      <Input
        placeholder="Search team or brokerage name..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {searching && (
        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 8, fontFamily: font.body }}>Searching...</div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {results.map((team) => {
            const alreadyRequested = mine.find((m) => m.team_id === team.id && m.status === "pending");
            return (
              <div
                key={team.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: 10, borderRadius: 8,
                  border: `1px solid ${selectedTeam?.id === team.id ? t.emerald : t.border}`,
                  background: selectedTeam?.id === team.id ? t.emerald + "10" : t.surfaceWarm,
                  cursor: alreadyRequested ? "default" : "pointer",
                  transition: "border-color 0.15s",
                }}
                onClick={() => !alreadyRequested && setSelectedTeam(team)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{team.name}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                    Owner: {team.owner_name}{team.owner_brokerage ? ` · ${team.owner_brokerage}` : ""}
                  </div>
                </div>
                {alreadyRequested ? (
                  <Badge color={t.amber}>Requested</Badge>
                ) : selectedTeam?.id === team.id ? (
                  <Badge color={t.emerald}>Selected</Badge>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {selectedTeam && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: t.textSecondary, marginBottom: 6, fontWeight: 600, fontFamily: font.body }}>
            Optional note to the team owner:
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hi! I'd like to join your team because..."
            rows={3}
            style={{
              width: "100%", background: t.surfaceWarm,
              border: `1.5px solid ${t.border}`, borderRadius: 6,
              padding: "8px 12px", color: t.textPrimary, fontSize: 12,
              outline: "none", fontFamily: font.body, resize: "vertical",
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button variant="primary" onClick={handleRequest} disabled={submitting}>
              {submitting ? "Sending..." : `Request to join ${selectedTeam.name}`}
            </Button>
            <Button variant="secondary" onClick={() => { setSelectedTeam(null); setMessage(""); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {mine.length > 0 && (
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, marginBottom: 10, fontFamily: font.display }}>
            Your Recent Requests
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {mine.map((r) => {
              const statusColor = {
                pending: t.amber, approved: t.emerald, denied: t.coral, cancelled: t.textMuted,
              }[r.status] || t.textMuted;
              return (
                <div key={r.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: 10, background: t.surfaceWarm,
                  border: `1px solid ${t.border}`, borderRadius: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{r.team_name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body }}>
                      {r.owner_name}{r.owner_brokerage ? ` · ${r.owner_brokerage}` : ""}
                    </div>
                  </div>
                  <Badge color={statusColor}>{r.status}</Badge>
                  {r.status === "pending" && (
                    <button
                      onClick={() => handleCancel(r.id)}
                      style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 11, color: t.textMuted, fontFamily: font.body }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {results.length === 0 && query.trim().length >= 2 && !searching && (
        <div style={{ marginTop: 12, fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          No teams matched "{query}". Ask the broker to invite you instead.
        </div>
      )}
    </Panel>
  );
}
